import type { FormikHelpers } from 'formik'
import type { FunctionComponent, VoidFunctionComponent } from 'react'

import {
	PrimaryButton,
	FileUpload,
	FileUploadHeadingWithTooltip,
	FlowContent,
	FlowFooter,
	FlowPage,
	FlowWrapper,
	H2,
	Note,
	SecondaryIconButton,
	TextArea,
} from '@monthio/design-system'
import { ArrowBack } from '@monthio/icons'
import { useFeatureFlag } from '@monthio/react-components'
import { useFormik, yupToFormErrors } from 'formik'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import * as Yup from 'yup'

import type { ApplicantProvidedDocumentType } from '../../types/authenticateSmartcheckSessionDto'
import type { IncomeVerificationFormData } from './IncomeVerification.types'

import { arraysAreEqual } from './Components/arraysAreEqual'
import { IncomeVerificationChain } from './Components/IncomeVerificationChain'
import { IncomeVerificationContent } from './Components/IncomeVerificationComponents'
import { incomeVerificationSchema } from './Components/incomeVerificationSchema'
import {
	emptyIncomeChain,
	sortIncomeChainsByAssessmentValue,
} from './Components/incomeVerificationUtils'
import {
	CategoryId,
	IncomeChainAssociationEnum,
} from './IncomeVerification.types'
import { useIncomeVerificationModule } from './useIncomeVerificationModule'

const FullWidthFlowContent = styled(FlowContent)`
	box-sizing: border-box;
	width: 100%;
`

const FileUploadWithMargin = styled(FileUpload)`
	margin-bottom: var(--spacing-xs);
`

const H2WithProperMargins = styled(H2)`
	margin: var(--spacing-l) 0 var(--spacing-m) 0;
`
const FlowFooterWithRowFlex = styled(FlowFooter)`
	flex-direction: row;
`
const FullHeightDiv = styled.div`
	height: 100%;
`

export const IncomeVerificationWrapper: VoidFunctionComponent = () => {
	const scrollRef = useRef<HTMLDivElement>(null)
	return (
		<FullHeightDiv>
			<div ref={scrollRef}></div>
			<IncomeVerification
				scrollToTop={() => scrollRef.current?.scrollIntoView()}
			/>
		</FullHeightDiv>
	)
}

const allowedFileTypes = [
	{ mime: 'image/jpeg', type: 'jpeg' },
	{ mime: 'image/jpg', type: 'jpg' },
	{ mime: 'image/jfif', type: 'jfif' },
	{ mime: 'image/png', type: 'png' },
	{ mime: 'image/bmp', type: 'bmp' },
	{ mime: 'image/webp', type: 'webp' },
	{ mime: 'application/pdf', type: 'pdf' },
	{ mime: 'application/msword', type: 'doc' },
	{
		mime: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
		type: 'docx',
	},
	{ mime: 'application/vnd.ms-excel', type: 'xls' },
	{
		mime: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
		type: 'xlsx',
	},
]

export const IncomeVerification: FunctionComponent<{
	scrollToTop: () => void
}> = (props) => {
	const { t } = useTranslation()
	const [
		expandAssociationOwnerQuestionaire,
		setExpandAssociationOwnerQuestionaire,
	] = useState<boolean>(false)
	const [mustUploadDocumentation, setMustUploadDocumentation] =
		useState<boolean>(false)
	const [doFileUpload, setDoFileUpload] = useState<boolean>(false)
	const {
		data: incomeVerification,
		finish,
		config,
	} = useIncomeVerificationModule()

	const documentConfigurations =
		config.applicantProvidedDocumentConfigurations ?? []

	const documentTypeToString = (type: ApplicantProvidedDocumentType): string =>
		t(
			`allowed-applicantProvidedDocumentType-${type[0]?.toUpperCase()}${type.slice(1)}`,
		)
	const documentTypeToTooltip = (type: ApplicantProvidedDocumentType): string =>
		t(
			`allowed-applicantProvidedDocumentType-${type[0]?.toUpperCase()}${type.slice(1)}-tooltip`,
		)
	const documentTypeToAddFile = (type: ApplicantProvidedDocumentType): string =>
		t(
			`allowed-applicantProvidedDocumentType-${type[0]?.toUpperCase()}${type.slice(1)}-add-file`,
		)

	useEffect(() => {
		formik.setFieldValue(
			'incomeChains',
			incomeVerification.incomeChains.length === 0
				? [emptyIncomeChain]
				: incomeVerification.incomeChains,
		)
	}, [incomeVerification.incomeChains])

	const onSubmit = useCallback(
		(
			values: IncomeVerificationFormData,
			formikHelpers: FormikHelpers<IncomeVerificationFormData>,
		) => {
			if (!mustUploadDocumentation && !config.shouldAllowFileUpload) {
				values.uploadedFiles = undefined
				values.categorizedDocuments = undefined
			}
			if (!values.userNote?.trim().length) {
				values.userNote = undefined
			}

			const incomeVerificationUpdateRequestDto: IncomeVerificationFormData = {
				...values,
				incomeChains: values.incomeChains.map((chain) => {
					return {
						...chain,
						owner: chain.owner ?? IncomeChainAssociationEnum.Mine,
						typeOfIncome:
							chain.typeOfIncome != null
								? {
										code: chain.typeOfIncome.code,
										description: chain.typeOfIncome.description,
									}
								: null,
						hasLargeInputDifferences: undefined,
					}
				}),
			}
			finish(incomeVerificationUpdateRequestDto)
			formikHelpers.setSubmitting(false)
		},
		[mustUploadDocumentation, config.shouldAllowFileUpload],
	)

	const hasFetchedIncomeChains = incomeVerification.incomeChains.length > 0

	const sortedIncomeChains = [...incomeVerification.incomeChains].sort(
		sortIncomeChainsByAssessmentValue,
	)

	// Danske bank can only bandle up to 21MB, for other clients we set the same limit as in the backend
	const maxFileSize = useFeatureFlag('limit-iv-documentation-file-size', true)
		? 20971520
		: 30000000
	const formik = useFormik({
		initialValues: {
			incomeChains: hasFetchedIncomeChains
				? sortedIncomeChains
				: [emptyIncomeChain],
			uploadedFiles: [],
			categorizedDocuments: [],
			userNote: '',
		} as IncomeVerificationFormData,
		onSubmit,
		validate: async (values) => {
			try {
				await incomeVerificationSchema({
					expandAssociationOwnerQuestionaire,
					mustUploadDocumentation,
					allowFileUpload: config.shouldAllowFileUpload || false,
					documentConfigurations,
					maxFileSize,
				}).validate(values, { abortEarly: false })
			} catch (error) {
				if (error instanceof Yup.ValidationError) {
					return yupToFormErrors(error)
				}
			}
		},
	})

	const { values, errors, handleSubmit, setFieldValue, handleChange } = formik
	const getCategorizedDocuments = (type: ApplicantProvidedDocumentType) =>
		values.categorizedDocuments
			?.filter(([_, docType]) => docType === type)
			.map(([file, _]) => file) ?? []
	const combineWithOtherCategorizedDocuments = (
		type: ApplicantProvidedDocumentType,
		files: readonly File[],
	) => [
		...(values.categorizedDocuments?.filter(
			([_, docType]) => docType !== type,
		) ?? []),
		...files.map((file) => [file, type]),
	]
	useEffect(() => {
		setMustUploadDocumentation(
			values.incomeChains.some((chain) => {
				const isEmptyChain = incomeVerification.incomeChains.length === 0
				const hasLargeInputDifference = chain.hasLargeInputDifferences
				return hasLargeInputDifference || isEmptyChain
			}),
		)
		const hasMoreThanOneSalaryIncomeType =
			incomeVerification.incomeChains.filter(
				(chain) => chain.typeOfIncome?.code === CategoryId.Salary,
			).length > 1
		setExpandAssociationOwnerQuestionaire(hasMoreThanOneSalaryIncomeType)
	}, [incomeVerification.incomeChains, values.incomeChains])

	useEffect(() => {
		formik.validateForm()
	}, [mustUploadDocumentation])
	const hasErrors = Object.keys(formik.errors).length !== 0

	const fileUploadTranslations = {
		addFileButtonAriaLabel: t('add-file'),
		isRequiredText: t('required'),
		removeFileButtonAriaLabel: (file: File) =>
			t('remove-named-file', { fileName: file.name }),
		sizeRestrictionText: t('incomeVerification-max-file-size'),
	}

	const handleContinue = useCallback(() => {
		if (mustUploadDocumentation || config.shouldAllowFileUpload) {
			setDoFileUpload(true)
			props.scrollToTop()
		} else {
			handleSubmit()
		}
	}, [mustUploadDocumentation, config.shouldAllowFileUpload, handleSubmit])

	if (doFileUpload) {
		const hasRequiredDocumentCategories = documentConfigurations.some(
			(docType) => docType.required,
		)
		const hasOptionalDocumentCategories = documentConfigurations.some(
			(docType) => !docType.required,
		)
		return (
			<FlowWrapper>
				<FullWidthFlowContent>
					<FlowPage.Header
						title={t('incomeVerification-upload-documentation')}
					/>
					{documentConfigurations.length != 0 ? (
						<>
							{hasRequiredDocumentCategories ? (
								<>
									<H2WithProperMargins>
										{t('incomeVerification-required-documentation')}
									</H2WithProperMargins>
									{documentConfigurations
										.filter((docType) => docType.required)
										.map((docType) => (
											<FileUploadWithMargin
												key={docType.applicantProvidedDocumentType}
												title={
													<FileUploadHeadingWithTooltip
														closeButtonText={t('close')}
														title={documentTypeToString(
															docType.applicantProvidedDocumentType,
														)}
														tooltip={documentTypeToTooltip(
															docType.applicantProvidedDocumentType,
														)}
													/>
												}
												translations={{
													...fileUploadTranslations,
													addFileButtonAriaLabel: documentTypeToAddFile(
														docType.applicantProvidedDocumentType,
													),
												}}
												acceptedFileTypes={allowedFileTypes}
												value={getCategorizedDocuments(
													docType.applicantProvidedDocumentType,
												)}
												onChange={(files) => {
													if (
														!arraysAreEqual(
															files,
															getCategorizedDocuments(
																docType.applicantProvidedDocumentType,
															),
														)
													) {
														setFieldValue(
															'categorizedDocuments',
															combineWithOtherCategorizedDocuments(
																docType.applicantProvidedDocumentType,
																files,
															),
														)
													}
												}}
											/>
										))}
								</>
							) : null}
							{hasOptionalDocumentCategories ? (
								<>
									{!(
										mustUploadDocumentation && !hasRequiredDocumentCategories
									) && (
										<H2WithProperMargins>
											{t('incomeVerification-optional-documentation')}
										</H2WithProperMargins>
									)}
									{documentConfigurations
										.filter((docType) => !docType.required)
										.map((docType) => (
											<FileUploadWithMargin
												key={docType.applicantProvidedDocumentType}
												title={
													<FileUploadHeadingWithTooltip
														closeButtonText={t('close')}
														title={documentTypeToString(
															docType.applicantProvidedDocumentType,
														)}
														tooltip={documentTypeToTooltip(
															docType.applicantProvidedDocumentType,
														)}
													/>
												}
												// I do not like, but it's the only way to remove the "is required" label :'(
												translations={{
													...fileUploadTranslations,
													isRequiredText: undefined,
													addFileButtonAriaLabel: documentTypeToAddFile(
														docType.applicantProvidedDocumentType,
													),
												}}
												acceptedFileTypes={allowedFileTypes}
												value={getCategorizedDocuments(
													docType.applicantProvidedDocumentType,
												)}
												onChange={(file) => {
													if (
														!arraysAreEqual(
															file,
															getCategorizedDocuments(
																docType.applicantProvidedDocumentType,
															),
														)
													) {
														setFieldValue(
															'categorizedDocuments',
															combineWithOtherCategorizedDocuments(
																docType.applicantProvidedDocumentType,
																file,
															),
														)
													}
												}}
											/>
										))}
								</>
							) : null}
						</>
					) : (
						<FileUploadWithMargin
							title={t('documents')}
							// I do not like, but it's the only way to remove the "is required" label :'(
							translations={{
								...fileUploadTranslations,
								isRequiredText: undefined,
							}}
							acceptedFileTypes={allowedFileTypes}
							value={values.uploadedFiles ?? []}
							onChange={(files) => {
								if (!arraysAreEqual(files, values.uploadedFiles)) {
									setFieldValue('uploadedFiles', files)
								}
							}}
						/>
					)}
				</FullWidthFlowContent>
				<FlowFooterWithRowFlex
					backButton={
						<SecondaryIconButton
							key="back"
							aria-label={t('incomeVerification-back-to-income-streams')}
							onClick={() => {
								setDoFileUpload(false)
								props.scrollToTop()
							}}
							ariaLabel={t('incomeVerification-fileUpload-back-button')}
							icon={<ArrowBack />}
						/>
					}
					actions={
						<PrimaryButton
							type="submit"
							disabled={hasErrors}
							key="submit"
							onClick={() => {
								handleSubmit()
							}}
						>
							{t('submit')}
						</PrimaryButton>
					}
				></FlowFooterWithRowFlex>
			</FlowWrapper>
		)
	}

	return (
		<FlowPage>
			<FlowPage.Content>
				<IncomeVerificationContent>
					<FlowPage.Header
						title={
							hasFetchedIncomeChains
								? t('incomeVerification-income-found-header-title')
								: t('incomeVerification-income-not-found-header-title')
						}
						description={
							hasFetchedIncomeChains
								? t(
										'incomeVerification-income-found-header-body-variability-other',
									)
								: t('incomeVerification-income-not-found-header-body')
						}
					/>
					{hasFetchedIncomeChains ? (
						incomeVerification.incomeChains.map((chain, chainIndex) => {
							return (
								<IncomeVerificationChain
									key={`INCOME_VERIFICATION_STREAM_ID_${chain.id}`}
									expandAssociationOwnerQuestionaire={
										expandAssociationOwnerQuestionaire
									}
									values={values}
									chainIndex={chainIndex}
									initialChain={chain}
									setFieldValues={setFieldValue}
									errors={errors}
								/>
							)
						})
					) : (
						<IncomeVerificationChain
							expandAssociationOwnerQuestionaire={
								expandAssociationOwnerQuestionaire
							}
							values={values}
							chainIndex={0}
							initialChain={emptyIncomeChain}
							setFieldValues={setFieldValue}
							errors={errors}
						/>
					)}
					{config.shouldShowConsumerNote ? (
						<TextArea
							label={t('incomeVerification-comment-label')}
							rows={4}
							fullWidth
							name="userNote"
							value={values.userNote}
							placeholder={t('incomeVerification-comment-placeholder')}
							onChange={handleChange}
						/>
					) : null}
				</IncomeVerificationContent>
			</FlowPage.Content>
			<FlowPage.Footer
				additionalContent={
					mustUploadDocumentation ? (
						<Note variant="warning" isContentAnnounced>
							{t('incomeVerification-must-upload-documentation')}
						</Note>
					) : config.shouldAllowFileUpload ? (
						<Note variant="info" isContentAnnounced>
							{t('incomeVerification-can-upload-documentation')}
						</Note>
					) : null
				}
				actions={
					<PrimaryButton
						disabled={!!formik.errors.incomeChains || !!formik.errors.userNote}
						onClick={handleContinue}
					>
						{mustUploadDocumentation || config.shouldAllowFileUpload
							? t('incomeVerification-continue-text')
							: t('submit')}
					</PrimaryButton>
				}
			/>
		</FlowPage>
	)
}
